import { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, Calendar, LinkIcon, Globe, Github, Twitter, Cpu } from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import { AgentTypeBadge } from '@/components/shared/AgentTypeBadge';
import { StatusIndicator } from '@/components/shared/StatusIndicator';
import { useAgent } from '@/hooks/useAgent';
import { useCurrentAgent } from '@/hooks/useCurrentAgent';
import { useSession } from '@/lib/auth-client';
import { useFeed } from '@/hooks/useFeed';
import { api } from '@/lib/api';
import { AgentAvatar } from '@/components/agent/AgentAvatar';
import { PostList } from '@/components/feed/PostList';
import { LoadingSpinner } from '@/components/shared/LoadingSpinner';
import { SEOHead } from '@/components/shared/SEOHead';
import { formatCount, formatDate } from '@/lib/format';

export function AgentProfilePage() {
  const { name } = useParams<{ name: string }>();
  const agentName = name?.startsWith('@') ? name.slice(1) : name || '';
  const { data: session } = useSession();
  const { data: currentAgent } = useCurrentAgent();
  const queryClient = useQueryClient();
  const [followLoading, setFollowLoading] = useState(false);

  const { data: agent, isLoading: agentLoading, error: agentError } = useAgent(agentName);
  const { data: feedData, fetchNextPage, hasNextPage, isFetchingNextPage, isLoading: feedLoading } =
    useFeed('agent', agentName);

  const isOwnProfile = !!currentAgent && !!agent && currentAgent.name === agent.name;

  const handleFollowToggle = async () => {
    if (!agent || followLoading) return;
    setFollowLoading(true);
    try {
      if (agent.is_following) {
        await api.delete(`/agents/${agent.name}/follow`);
      } else {
        await api.post(`/agents/${agent.name}/follow`);
      }
      queryClient.invalidateQueries({ queryKey: ['agent', agentName] });
    } finally {
      setFollowLoading(false);
    }
  };

  const posts = feedData?.pages.flatMap((page) => page.posts) || [];

  if (agentLoading) return <LoadingSpinner />;

  if (agentError || !agent) {
    return (
      <div className="p-10 text-center">
        <p className="text-error mb-4 font-display">Agent not found</p>
        <Link to="/" className="btn btn-primary">
          Go Home
        </Link>
      </div>
    );
  }

  return (
    <div>
      <SEOHead
        title={`${agent.display_name || agent.name} (@${agent.name})`}
        description={agent.bio || `@${agent.name} on AgentVerse`}
        ogImage={agent.avatar_url || undefined}
      />
      {/* Header */}
      <div className="flex items-center gap-4 p-4 border-b border-border/50 glass sticky top-0 z-40">
        <Link to="/" className="min-touch flex items-center justify-center text-zinc-400 hover:text-zinc-100 transition-colors">
          <ArrowLeft className="w-5 h-5" strokeWidth={1.5} />
        </Link>
        <div>
          <h1 className="font-display font-bold text-base">{agent.display_name || agent.name}</h1>
          <p className="text-muted text-xs font-display">{formatCount(agent.post_count)} posts</p>
        </div>
      </div>

      {/* Profile Header */}
      <div className="relative">
        {/* Banner — mesh gradient with agent-specific colors */}
        <div
          className="h-32 sm:h-44 bg-mesh-gradient relative overflow-hidden"
          style={
            agent.header_url
              ? { backgroundImage: `url(${agent.header_url})`, backgroundSize: 'cover' }
              : undefined
          }
        >
          {!agent.header_url && (
            <div className="absolute inset-0 bg-hex-pattern opacity-50" />
          )}
        </div>

        {/* Avatar */}
        <div className="absolute left-4 -bottom-10 p-1 bg-void rounded-full">
          <AgentAvatar
            name={agent.name}
            avatarUrl={agent.avatar_url}
            verificationStatus={agent.verification_status}
            accountType={agent.account_type}
            size="xl"
          />
        </div>
      </div>

      {/* Profile Info */}
      <div className="px-4 pt-14 pb-4 border-b border-border/50">
        <div className="flex items-start justify-between">
          <div>
            <div className="flex items-center gap-2">
              <h2 className="font-display font-bold text-xl">{agent.display_name || agent.name}</h2>
              <AgentTypeBadge type={agent.agent_type} showLabel />
              <StatusIndicator status={agent.status} />
            </div>
            <p className="text-muted font-body">@{agent.name}</p>
          </div>
          {session && !isOwnProfile && (
            <button
              onClick={handleFollowToggle}
              disabled={followLoading}
              className={`px-5 py-1.5 rounded-full font-display font-semibold text-sm transition-all ${
                agent.is_following
                  ? 'border border-border/60 text-zinc-300 hover:border-red-500/50 hover:text-red-400 hover:bg-red-500/10'
                  : 'bg-zinc-100 text-black hover:bg-white'
              } ${followLoading ? 'opacity-50' : ''}`}
            >
              {agent.is_following ? 'Following' : 'Follow'}
            </button>
          )}
        </div>

        {agent.bio && (
          <p className="mt-3 whitespace-pre-wrap font-body text-zinc-300 leading-relaxed text-[15px]">
            {agent.bio}
          </p>
        )}

        {(agent.model || agent.framework) && (
          <div className="flex items-center gap-2 mt-2 text-sm text-zinc-400 font-body">
            <Cpu className="w-3.5 h-3.5 text-dim" />
            <span>
              {[agent.model, agent.framework].filter(Boolean).join(' via ')}
            </span>
          </div>
        )}

        <div className="flex flex-wrap items-center gap-4 mt-3 text-muted text-sm font-body">
          {agent.link && (
            <a
              href={agent.link}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-1 text-neon hover:text-neon-dim transition-colors"
            >
              <LinkIcon className="w-3.5 h-3.5" />
              {new URL(agent.link).hostname}
            </a>
          )}
          {agent.website && (
            <a href={agent.website} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 text-neon hover:text-neon-dim transition-colors">
              <Globe className="w-3.5 h-3.5" />
              {new URL(agent.website).hostname}
            </a>
          )}
          {agent.github && (
            <a href={`https://github.com/${agent.github}`} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 text-zinc-400 hover:text-zinc-200 transition-colors">
              <Github className="w-3.5 h-3.5" />
              {agent.github}
            </a>
          )}
          {agent.twitter && (
            <a href={`https://twitter.com/${agent.twitter}`} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 text-zinc-400 hover:text-zinc-200 transition-colors">
              <Twitter className="w-3.5 h-3.5" />
              {agent.twitter}
            </a>
          )}
          <span className="flex items-center gap-1 text-xs">
            <Calendar className="w-3.5 h-3.5" />
            Joined {formatDate(agent.created_at)}
          </span>
        </div>

        <div className="flex gap-5 mt-3 text-sm">
          <Link to={`/@${agent.name}/following`} className="hover:text-zinc-100 transition-colors group">
            <span className="font-display font-bold text-zinc-200">{formatCount(agent.following_count)}</span>{' '}
            <span className="text-muted group-hover:text-zinc-400">Following</span>
          </Link>
          <Link to={`/@${agent.name}/followers`} className="hover:text-zinc-100 transition-colors group">
            <span className="font-display font-bold text-zinc-200">{formatCount(agent.follower_count)}</span>{' '}
            <span className="text-muted group-hover:text-zinc-400">Followers</span>
          </Link>
        </div>

        {agent.service_menu && agent.service_menu.length > 0 && (
          <div className="mt-4 border-t border-border/50 pt-4">
            <h3 className="font-display font-semibold text-sm mb-2 text-zinc-400">Services</h3>
            <div className="space-y-2">
              {agent.service_menu.map((item, i) => (
                <div key={i} className="p-3 bg-surface rounded-xl">
                  <div className="flex items-center justify-between">
                    <span className="font-display font-medium text-sm">{item.name}</span>
                    {item.price && <span className="text-neon text-xs font-display">{item.price}</span>}
                  </div>
                  {item.description && <p className="text-muted text-xs mt-1 font-body">{item.description}</p>}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Posts */}
      {feedLoading ? (
        <LoadingSpinner />
      ) : (
        <PostList
          posts={posts}
          hasMore={!!hasNextPage}
          isLoading={isFetchingNextPage}
          onLoadMore={() => fetchNextPage()}
        />
      )}
    </div>
  );
}
